import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = "4POS eCommerce - Multi-vendor Marketplace",
  description = "Discover amazing products from trusted sellers. Shop with confidence on our secure multi-vendor marketplace.",
  keywords = "ecommerce, marketplace, online shopping, products, sellers",
  image = "/images/logo.png",
  url = "",
  type = "website",
  siteName = "4POS eCommerce",
  product = null,
  organization = null,
  breadcrumbs = null,
  noindex = false,
  canonical = null
}) => {
  // Generate structured data for products
  const generateProductStructuredData = () => {
    if (!product) return null;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description || product.name,
      "image": product.images && product.images.length > 0 ? product.images.map(img => getProxiedUrl(img)) : [],
      "brand": {
        "@type": "Brand",
        "name": product.brand || "Unknown Brand"
      },
      "offers": {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": product.currency || "USD",
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": product.shopName || "Store"
        }
      }
    };

    // Add rating if available
    if (product.rating && product.rating > 0) {
      structuredData.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "bestRating": 5,
        "worstRating": 1,
        "ratingCount": 1 // Default count, could be enhanced with actual review count
      };
    }

    // Add SKU if available
    if (product._id) {
      structuredData.sku = product._id;
    }

    return structuredData;
  };

  // Generate structured data for organization
  const generateOrganizationStructuredData = () => {
    if (!organization) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": organization.name || "4POS eCommerce",
      "url": organization.url || window.location.origin,
      "logo": organization.logo || "/images/logo.png",
      "description": organization.description || "Multi-vendor ecommerce marketplace",
      "sameAs": organization.socialLinks || []
    };
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbStructuredData = () => {
    if (!breadcrumbs || !Array.isArray(breadcrumbs)) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  };

  // Helper function to get proxied URL (reuse existing utility)
  const getProxiedUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/dashboard/product/proxy-image/')) return url;
    return url;
  };

  // Generate final title
  const finalTitle = product 
    ? (product.seoTitle || `${product.name} - ${siteName}`)
    : title;

  // Generate final description
  const finalDescription = product 
    ? (product.seoDescription || `${product.description || product.name} - Shop now at ${Number(product.price).toFixed(2)} ${product.currency || 'USD'}. ${product.stock > 0 ? 'In stock' : 'Out of stock'}.`)
    : description;

  // Generate final URL
  const finalUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  // Generate canonical URL
  const canonicalUrl = canonical || finalUrl;

  // Generate final image
  const finalImage = product 
    ? (product.seoImage || (product.images && product.images.length > 0 ? getProxiedUrl(product.images[0]) : "/images/logo.png"))
    : image;

  // Generate final keywords
  const finalKeywords = product 
    ? (product.seoKeywords || `${product.name}, ${product.brand}, ${product.category}, online shopping, ${siteName}`)
    : keywords;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Product-specific Open Graph tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={Number(product.price).toFixed(2)} />
          <meta property="product:price:currency" content={product.currency || "USD"} />
          <meta property="product:availability" content={product.stock > 0 ? "in stock" : "out of stock"} />
          <meta property="product:brand" content={product.brand || "Unknown Brand"} />
        </>
      )}

      {/* Structured Data */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify(generateProductStructuredData())}
        </script>
      )}

      {organization && (
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationStructuredData())}
        </script>
      )}

      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify(generateBreadcrumbStructuredData())}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
